import { Input, PasswordInput } from "@mantine/core";
import { type CSSProperties, useRef } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { usePostApi } from "src/hooks";
import { UserSettingsContainer } from "src/pages/Settings/AccountDetails/AccountDetails.styled";
import { getUserId, titlecase } from "src/utils";
import { Notification } from "src/utils/Notification";

import CrewAuthButton from "./CrewSetUp/UserForm/CrewAuthButton";

interface IAuthFieldForm {
  value: string;
  repeatValue: string;
  authpw: string;
}

interface Props {
  type: string;
  userId?: string;
  style?: CSSProperties;
  minLength?: number;
  maxLength?: number;
}

const AuthFieldForm = ({
  type,
  userId,
  style,
  minLength,
  maxLength,
}: Props) => {
  const { post, isLoading } = usePostApi();

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    trigger,
  } = useForm<IAuthFieldForm>({
    defaultValues: { value: "", repeatValue: "", authpw: "" },
  });

  const first = useRef({});
  first.current = watch("value", "");

  const getRequestData = () => {
    switch (type) {
      case "password":
        return "user-save-password";
      case "pin":
        return "user-save-pin";
      case "passphrase":
        return "user-save-passphrase";
      default:
        return "";
    }
  };

  const submit: SubmitHandler<IAuthFieldForm> = async data => {
    await post({
      request: getRequestData(),
      body: {
        params: {
          userid: userId ?? getUserId(),
          [type]: data.value,
          authpw: data.authpw,
        },
      },
      onSuccess: () => {
        Notification(
          "Success",
          `${titlecase(type)} has been successfully changed.`,
          "green",
        );
        reset();
      },
    });
  };

  const onSubmit = async (authpw: string) => {
    setValue("authpw", authpw);
    await handleSubmit(submit)();
  };

  const messageForEqualLengths = `⚠ ${titlecase(
    type,
  )} must be ${minLength} characters long`;
  const inputProps =
    minLength && maxLength
      ? {
          minLength: {
            value: minLength,
            message:
              minLength == maxLength
                ? messageForEqualLengths
                : `⚠ ${titlecase(
                    type,
                  )} must be at least ${minLength} characters`,
          },
          maxLength: {
            value: maxLength,
            message:
              minLength == maxLength
                ? messageForEqualLengths
                : `⚠ ${titlecase(
                    type,
                  )} must be less than ${maxLength} characters`,
          },
        }
      : {};
  return (
    <UserSettingsContainer style={style}>
      <Input.Wrapper w={"100%"} label={`New ${titlecase(type)}`}>
        <PasswordInput
          {...register("value", {
            required: "⚠ This field is required",
            ...inputProps,
          })}
          mr={10}
          inputMode="numeric"
          error={errors.value?.message}
        />
      </Input.Wrapper>
      <Input.Wrapper w={"100%"} label={`Repeat ${titlecase(type)}`}>
        <PasswordInput
          {...register("repeatValue", {
            required: "⚠ This field is required",

            validate: value =>
              value === first.current ||
              `⚠ The ${titlecase(type)}s do not match`,
          })}
          mr={10}
          error={errors.repeatValue?.message}
        />
      </Input.Wrapper>
      <CrewAuthButton
        onSubmit={onSubmit}
        variant="outline"
        size="xs"
        ml={10}
        mt={26}
        loading={isLoading}
        validate={trigger}>
        Save
      </CrewAuthButton>
    </UserSettingsContainer>
  );
};

export default AuthFieldForm;
